import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Info, RefreshCw, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { BackupCodesGrid } from '@/features/mfa/components/backup-codes-grid';
import { MfaStatusCard } from '@/features/mfa/components/mfa-status-card';
import { TrustedDevicesList } from '@/features/mfa/components/trusted-devices-list';
import { useMfaStatus, useRegenerateBackupCodes } from '@/features/mfa/hooks/use-mfa';

export const Route = createFileRoute('/_app/settings/security')({
  component: SecurityPage,
});

function SecurityPage() {
  const { data: status, isLoading } = useMfaStatus();

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Seguridad</h1>
        <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
          Autenticación de 2 factores, códigos de respaldo, y dispositivos confiados.
        </p>
      </header>

      <MfaStatusCard status={status} isLoading={isLoading} />

      {status?.enrolled ? <EnrolledSections /> : <UnenrolledCta />}

      <RecoveryInfoCard />
    </div>
  );
}

function UnenrolledCta() {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center justify-between rounded-lg border p-5"
      style={{
        background: 'var(--color-bg-elevated)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div>
        <h2 className="text-sm font-semibold">Activá MFA ahora</h2>
        <p className="mt-0.5 text-xs text-[color:var(--color-fg-muted)]">
          5 minutos. Vas a necesitar una app authenticator instalada en tu teléfono.
        </p>
      </div>
      <Button onClick={() => void navigate({ to: '/auth/mfa/enroll' })}>
        <ShieldCheck className="mr-2 h-4 w-4" aria-hidden />
        Activar MFA
      </Button>
    </div>
  );
}

function EnrolledSections() {
  return (
    <>
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Códigos de respaldo</h2>
          <RegenerateBackupCodesButton />
        </div>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Regenerar invalida la tanda anterior. Hacelo si perdiste tu lista o si te quedan pocos códigos.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Dispositivos confiados</h2>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Cuando marcás "Confiar en este dispositivo" al verificar, se guarda una cookie de 30 días que te evita el código MFA en sesiones siguientes desde el mismo navegador. Revocá cualquiera que ya no uses.
        </p>
        <TrustedDevicesList />
      </section>
    </>
  );
}

function RegenerateBackupCodesButton() {
  const [stage, setStage] = useState<'idle' | 'confirming' | 'showing'>('idle');
  const [newCodes, setNewCodes] = useState<readonly string[]>([]);
  const regen = useRegenerateBackupCodes();

  const confirm = async (): Promise<void> => {
    try {
      const res = await regen.mutateAsync();
      setNewCodes(res.backup_codes);
      setStage('showing');
    } catch {
      toast.error('No pudimos regenerar los códigos. Probá de nuevo.');
      setStage('idle');
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setStage('confirming')}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden />
        Regenerar
      </Button>

      <Dialog open={stage === 'confirming'} onOpenChange={(o) => !o && setStage('idle')}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Regenerar códigos de respaldo</DialogTitle>
            <DialogDescription>
              Esto invalida los 10 códigos actuales y genera 10 nuevos. Si tenés
              códigos guardados en algún lado, vas a tener que reemplazarlos. ¿Continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStage('idle')}>
              Cancelar
            </Button>
            <Button onClick={() => void confirm()} disabled={regen.isPending}>
              {regen.isPending ? <Spinner size="sm" /> : 'Sí, regenerar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stage === 'showing'} onOpenChange={(o) => !o && setStage('idle')}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Nuevos códigos de respaldo</DialogTitle>
            <DialogDescription>
              Guardalos ahora — no los vas a volver a ver. Tus códigos anteriores quedaron invalidados.
            </DialogDescription>
          </DialogHeader>
          <BackupCodesGrid codes={newCodes} />
          <DialogFooter>
            <Button onClick={() => setStage('idle')}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RecoveryInfoCard() {
  return (
    <div
      className="rounded-lg border p-5"
      style={{
        background: 'var(--color-bg-overlay)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--color-fg-muted)]" aria-hidden />
        <div className="text-sm">
          <h3 className="mb-2 font-medium">¿Cómo recuperas acceso si pierdes tu app?</h3>
          <ol className="ml-4 list-decimal space-y-1 text-[color:var(--color-fg-muted)]">
            <li>Usa uno de tus códigos de respaldo en la pantalla de verificación.</li>
            <li>Si tienes un dispositivo confiado activo, inicia sesión desde ese navegador.</li>
            <li>Pídele a un owner de la organización que resetee tu MFA (Equipo → menú del miembro → Resetear MFA).</li>
            <li>Como último recurso, contacta a soporte para una recuperación manual con validación de identidad.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
